import React from "react";
import {
  MessageCircle,
  Instagram,
  Send as Telegram,
  Youtube,
  Facebook,
  Music2,
  Twitter,
  Globe,
  ArrowUpRight,
} from "lucide-react";

/**
 * SocialNetworksBar — visible on every client tab in the Home layout.
 * Renders the admin-managed list of active social networks with the AG7
 * neon-cyan aesthetic and mobile-friendly tap targets.
 *
 * Icon strategy:
 *   • If the admin stored an emoji (e.g. "💬"), render it as-is.
 *   • Otherwise, auto-pick a Lucide icon from the network name / URL
 *     (WhatsApp, Instagram, TikTok, Telegram, YouTube, Facebook,
 *      Twitter/X). Fallback: Globe.
 */

const EMOJI_RE = /\p{Extended_Pictographic}/u;

function pickLucideIcon(name = "", url = "") {
  const hay = `${name} ${url}`.toLowerCase();
  if (/(whatsapp|wa\.me|api\.whatsapp)/i.test(hay)) return MessageCircle;
  if (/instagram|instagr\.am/i.test(hay)) return Instagram;
  if (/tiktok/i.test(hay)) return Music2;
  if (/telegram|t\.me/i.test(hay)) return Telegram;
  if (/youtube|youtu\.be/i.test(hay)) return Youtube;
  if (/facebook|fb\.com|fb\.me/i.test(hay)) return Facebook;
  if (/twitter|x\.com/i.test(hay)) return Twitter;
  return Globe;
}

export function SocialNetworksBar({ networks = [] }) {
  if (!networks || networks.length === 0) return null;

  return (
    <section
      data-testid="social-networks-bar"
      className="relative mt-8 rounded-3xl overflow-hidden border border-cyan-500/30 bg-gradient-to-br from-[#0a1220] via-[#0a0d18] to-[#120a1e] px-4 py-6 sm:px-6 sm:py-7"
    >
      {/* Ambient neon glow */}
      <div className="pointer-events-none absolute inset-0 opacity-60" aria-hidden="true">
        <div className="absolute top-0 left-0 w-48 h-48 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-fuchsia-500/15 blur-3xl" />
      </div>

      <div className="relative flex flex-col items-center gap-4 text-center">
        <div className="space-y-1">
          <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.35em] text-cyan-300/80">
            Comunidad AG7
          </p>
          <h3
            data-testid="social-bar-heading"
            className="text-lg sm:text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-white to-fuchsia-300 leading-tight"
          >
            ¡Síguenos en nuestras redes sociales!
          </h3>
          <p className="text-[11px] sm:text-xs text-zinc-400 font-mono">
            Promos, sorteos y avisos en vivo · No te pierdas nada
          </p>
        </div>

        <div
          data-testid="social-bar-links"
          className="flex flex-wrap justify-center gap-2 sm:gap-3 w-full"
        >
          {networks.map((net) => {
            const hasEmoji = !!(net.icon && EMOJI_RE.test(net.icon));
            const LucideIcon = hasEmoji ? null : pickLucideIcon(net.name, net.url);
            return (
              <a
                key={net.id}
                href={net.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-link-${net.id}`}
                aria-label={`Abrir ${net.name}`}
                className="group relative inline-flex items-center gap-2 min-h-[44px] px-4 py-2.5 rounded-2xl bg-white/[0.04] hover:bg-cyan-500/15 border border-cyan-400/40 hover:border-cyan-300 text-cyan-100 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all backdrop-blur-sm shadow-[0_0_18px_-8px_rgba(34,211,238,0.6)] hover:shadow-[0_0_25px_-4px_rgba(34,211,238,0.85)] active:scale-95"
              >
                <span className="w-6 h-6 rounded-lg bg-cyan-500/15 border border-cyan-400/40 flex items-center justify-center flex-shrink-0">
                  {hasEmoji ? (
                    <span className="text-sm leading-none">{net.icon}</span>
                  ) : (
                    <LucideIcon className="w-3.5 h-3.5 text-cyan-300 group-hover:text-white transition-colors" />
                  )}
                </span>
                <span className="whitespace-nowrap">{net.name}</span>
                <ArrowUpRight className="w-3 h-3 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default SocialNetworksBar;
